import React, { useContext, useEffect, useState } from 'react';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import IngredientCard from './IngredientCard/IngredientCard';
import FormContext from '../../Form/FormContext';
import { getIngredientEntriesByIds } from '../../../features/IngredientEntryContainer/selectors';
import { getConfigs, getLanguage } from 'features/app/selectors';
import { SortableContainer } from 'react-sortable-hoc';
import arrayMove from 'array-move';

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'baseline'
  },
  ingredientCounts: {
    display: 'inline',
    fontWeight: 100
  },
  ingredientsCompletedText: {
    display: 'inline',
    marginRight: theme.spacing(1),
    textTransform: 'uppercase'
  },
  dragging: {
    pointerEvents: 'auto !important',
    cursor: 'grabbing !important'
  }
}));

function getPpiDetailDefaultData(itemCode) {
  return {
    itemCode,
    ppiEnrichmentDataId: null
  };
}

function recalculateStepNumbers(steps) {
  return steps.map((step, index) => {
    return { ...step, lineId: index + 1 };
  });
}

function findDuplicates(arr, key) {
  const seen = new Set();
  const duplicates = [];

  for (const item of arr) {
    const keyValue = item[key];
    if (seen.has(keyValue)) {
      duplicates.push(item);
    } else {
      seen.add(keyValue);
    }
  }

  return duplicates;
}

function sortfunction(ids, data) {
  const result = [];
  for (let i = 0; i < ids.length; i++) {
    const value = data.filter(j => j.itemCode === ids[i]);
    result.push(...value);
  }
  return result;
}

const IngredientsTabContent = () => {
  const classes = useStyles();
  const language = useSelector(getLanguage);
  const { data, setFieldValue, getFieldValue } = useContext(FormContext);

  const {
    ppiDetails = [],
    ingredientEntryIds,
    referencedIFoodData
  } = data;

  const ingredientIds = getFieldValue('ingredientEntryIds') || [];
  const ingredientEntries = useSelector(
    getIngredientEntriesByIds(ingredientIds)
  ).toJS();
const franchiseType = useSelector(getConfigs).toJS().franchise;
  const sortedByLine = _.sortBy(ingredientEntries, 'lineId');
  const referenceData =
    referencedIFoodData && referencedIFoodData.referencedIngredientEntities || null;

  const idsFromRef =
    referenceData !== null ? referenceData.map(i => i.itemCode) : [];



// initialize state once
const [sortedIngredient, setSortedIngredient] = useState(
  referencedIFoodData && referencedIFoodData.referencedIngredientEntities
    ? _.sortBy(referencedIFoodData.referencedIngredientEntities, 'lineId')
    : _.sortBy(ingredientEntries, 'lineId')
);

// Sync local state with FormContext when component mounts or referencedIFoodData changes
useEffect(() => {
  // Prefer reference data if available, else fallback to ingredientEntries
  const refIngredients =
    referencedIFoodData?.referencedIngredientEntities || ingredientEntries;

  // Always sort by lineId when new data arrives
  const sorted = _.sortBy(refIngredients, 'lineId');

  // Update local state only if changed
  if (!_.isEqual(sorted, sortedIngredient)) {
    setSortedIngredient(sorted);
  }
}, [referencedIFoodData, ingredientEntries]);
useEffect(() => {
  if (data.referencedIFoodData) {
    const currentItemCodes = data.referencedIFoodData.referencedIngredientEntities.map(i => i.itemCode);
    const filteredPpiDetails = ppiDetails.filter(ppi => currentItemCodes.includes(ppi.itemCode));
    setFieldValue('ppiDetails', filteredPpiDetails);
  }
}, [data.referencedIFoodData]);

  // ----------------------------
  // PPI
  // ----------------------------
  function getPpiDetailByItemCode(byItemCode) {
    const details = ppiDetails.filter(
      ppiDetail => ppiDetail.itemCode === byItemCode
    );
    let ppiDetail;
    if (details.length > 0) {
      [ppiDetail] = details;
    } else {
      ppiDetail = getPpiDetailDefaultData(byItemCode);
      ppiDetails.push(ppiDetail);
    }
    return ppiDetail;
  }

  function onChangeSelectedPpi(itemCode, event) {
    const ppiEnrichmentDataId = event.target.value;

    const ppiDetail = getPpiDetailByItemCode(itemCode);
    if (event.target.value === 'true' || event.target.value === 'false') {
      ppiDetail.ppiEnrichmentDataId = '';
      ppiDetail.ppiNotRequired = !ppiDetail.ppiNotRequired;
      setFieldValue('ppiDetails', ppiDetails);
    } else {
      ppiDetail.ppiEnrichmentDataId =
        ppiDetail.ppiEnrichmentDataId === ppiEnrichmentDataId
          ? ''
          : ppiEnrichmentDataId;
      ppiDetail.ppiNotRequired = false;
      setFieldValue('ppiDetails', ppiDetails);
    }
  }

  function isIngredientCompleted(itemCode) {
    const ppiDetail = getPpiDetailByItemCode(itemCode);
    return !!ppiDetail.ppiEnrichmentDataId || ppiDetail.ppiNotRequired;
  }

  function ingredientCount() {
    return sortedIngredient.length;
  }

  function ingredientsCompleted() {
    return sortedIngredient.reduce(
      (count, ingredientEntry) =>
        isIngredientCompleted(ingredientEntry.itemCode) ? count + 1 : count,
      0
    );
  }

  // ----------------------------
  // SORT END
  // ----------------------------
  function sortEnd({ oldIndex, newIndex }) {
    if (sortedIngredient.length < 2) {
      return;
    }

    let rearrangedSteps = arrayMove(sortedIngredient, oldIndex, newIndex);
    rearrangedSteps = recalculateStepNumbers(rearrangedSteps);

    const sentData = rearrangedSteps.map(i => i.id);
    const getItemCode = rearrangedSteps.map(i => i.itemCode);

    const IngredientEntities = {
      ...data.referencedIFoodData,
      referencedIngredientEntities: rearrangedSteps
    };

    const reorderedPpi = sortfunction(getItemCode, ppiDetails);

    setFieldValue('referencedIFoodData', IngredientEntities);
    setFieldValue('ppiDetails', reorderedPpi);
    setFieldValue('ingredientEntryIds', sentData);

    setSortedIngredient(rearrangedSteps); // ⭐ Update state
  }

  // ----------------------------
  // QUANTITY OUT UPDATE
  // ----------------------------
  const handleChangeQtyOut = (e, field, ingredientId) => {
  const updatedIngredients = sortedIngredient.map(a => {
    if (a.id === ingredientId) {
      return {
        ...a,
        amount: {
          ...a.amount,
          metric: {
            ...a.amount.metric,
            [field]: e.target.value
          }
        }
      };
    }
    return a;
  });

  // update local state for UI
  setSortedIngredient(updatedIngredients);

  // update FormContext — source of truth
  const updatedReferencedData = {
    ...data.referencedIFoodData,
    referencedIngredientEntities: updatedIngredients
  };

  setFieldValue('referencedIFoodData', updatedReferencedData);

  // optional: if other FormContext fields depend on it, update them too
  setFieldValue('ingredientEntryList', updatedIngredients);
  setFieldValue('dish', {
    ...data.dish,
    ingredientEntryList: updatedIngredients
  });
};
const handleChangeBomInfoTextShort = (e, ingredientId) => {
  const newTextShort = e.target.value;

  const updatedIngredients = sortedIngredient.map(a => {
    if (a.id === ingredientId) {
      return {
        ...a,
        amount: {
          ...a.amount,
          metric: {
            ...a.amount.metric,
            displayInfo: {
              ...a.amount.metric.displayInfo,
              [language]: {
                ...a.amount.metric.displayInfo[language],
                textShort: newTextShort
              }
            }
          }
        }
      };
    }
    return a;
  });

  // update local state for UI
  setSortedIngredient(updatedIngredients);

  // update FormContext — source of truth
  const updatedReferencedData = {
    ...data.referencedIFoodData,
    referencedIngredientEntities: updatedIngredients
  };

  setFieldValue('referencedIFoodData', updatedReferencedData);

  // same pattern as your QtyOut handler
  setFieldValue('ingredientEntryList', updatedIngredients);
  setFieldValue('dish', {
    ...data.dish,
    ingredientEntryList: updatedIngredients
  });
};

console.log(data,"data")
console.log(sortedIngredient,"sortedIngredient");


  let duplicatedPpis = [];
  if (data && data.referencedIFoodData) {
    duplicatedPpis = findDuplicates(
      data.referencedIFoodData.referencedIngredientEntities,
      'itemCode'
    );
  }

  return (
    <>
      <div className={classes.header}>
        <Typography
          variant="body1"
          classes={{ root: classes.ingredientsCompletedText }}
        >
          <FormattedMessage id="ppsi.ingredientsHandled" />
        </Typography>
        <Typography variant="h5" classes={{ root: classes.ingredientCounts }}>
          {ingredientsCompleted()}/{ingredientCount()}
        </Typography>
      </div>

      <SortableList
        onSortEnd={sortEnd}
        axis="y"
        distance={5}
        helperClass={classes.dragging}
        shouldCancelStart={e => {
          return duplicatedPpis.length;
        }}
      >
        <div>
          {sortedIngredient.map((ingredientEntry, index) => {
            const ppiSelection = getPpiDetailByItemCode(
              ingredientEntry.itemCode
            );

            return (
              <IngredientCard
                index={index}
                legacyFormulaCode={ingredientEntry.legacyFormulaCode}
                ingredientName={ingredientEntry.presentationName[language]}
                itemCode={ingredientEntry.itemCode}
                globalIngredientId={ingredientEntry.globalIngredientId}
                quantity={ingredientEntry.quantity}
                quantityUom={ingredientEntry.uomCode}
                owningOrganization={ingredientEntry.owningOrganization}
                ppis={ingredientEntry.ppis}
                isExcluded={ppiSelection.ppiNotRequired}
                selectedPpiDataId={ppiSelection.ppiEnrichmentDataId}
                completed={isIngredientCompleted(ingredientEntry.itemCode)}
                onChangeSelectedPpi={event => {
                  onChangeSelectedPpi(ingredientEntry.itemCode, event);
                }}
                key={ingredientEntry.id}
                {...(franchiseType === "NIR"
                  ? {
                      uomQty: ingredientEntry.amount &&
                      ingredientEntry.amount.metric &&
                      ingredientEntry.amount.metric.quantityOut,
                      uomCode: ingredientEntry.amount &&
                      ingredientEntry.amount.metric &&
                      ingredientEntry.amount.metric.info &&
                      ingredientEntry.amount.metric.info[language]
                        ? ingredientEntry.amount.metric.info[language].textShort
                        : '',
                      displayQuantityOut: ingredientEntry.amount &&
                      ingredientEntry.amount.metric &&
                      ingredientEntry.amount.metric.displayQuantityOut,
                      displayBomUomLabel:
                      ingredientEntry.amount &&
                      ingredientEntry.amount.metric &&
                      ingredientEntry.amount.metric.displayInfo &&
                      ingredientEntry.amount.metric.displayInfo[language]
                        ? ingredientEntry.amount.metric.displayInfo[language].textShort
                        : ''
                    }
                  : {}
                )}
                handleChangeQtyOut={handleChangeQtyOut}
                ingredientId={ingredientEntry.id}
                handleChangeBomInfoTextShort={handleChangeBomInfoTextShort}
                franchiseType={franchiseType}
              />
            );
          })}
        </div>
      </SortableList>
    </>
  );
};

const SortableList = SortableContainer(({ children }) => {
  return children;
});

export default IngredientsTabContent;
